import React, { useState, useEffect } from "react";
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { API_URL } from '../apiConfig';
import { useSelector } from "react-redux";

import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CustomTypography from './UI/Typography/CustomTypography';
import NewsComplexCard from './UI/Cards/NewsComplexCard';
import CustomPager from './UI/Pagination/CustomPager';

const theme = createTheme({
  shape: {
    borderRadius: 5
  },
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#F9F9F9',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,

  display: 'flex', // Make it a flex container
  flexDirection: 'column', // Align children in a column
  alignItems: 'center', // Center items horizontally
  justifyContent: 'center', // Center items vertically
}));

const News = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const currentUser = useSelector((state) => state.auth.user);
  const [newsIds, setNewsIds] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedNews = latestNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  //Fetch ONLY the ids of all news to fill the pager 
  const fetchAllNewsIds = async () => {
    try {
      const response = await axios.get(API_URL + '/news/ids');
      const idsArray = response.data.map(newsItem => newsItem.id);
      setNewsIds(idsArray);
      setTotalItems(idsArray.length);
    } catch (error) {
      console.error("Error fetching all news IDs:", error);
      // Handle error if needed
    }
  };

  //Then get 4 News per page 
  const fetchNews = async () => {
    try {
      const response = await axios.get(API_URL + '/news/paged', {
        params: {
          page: currentPage,
          itemsPerPage: itemsPerPage,
        },
      });

      // Access the 'rows' property in the response
      const newsArray = response.data.rows.map((newsItem) => ({
        id: newsItem.id,
        title: newsItem.title,
        shortDescription: newsItem.shortDescription,
        image: newsItem.image,
        imageURL: newsItem.imageURL,
      }));

      // Sort newsArray by ID in descending order
      const sortedNewsArray = newsArray.sort((a, b) => b.id - a.id);
      // Get the latest news items
      setLatestNews(sortedNewsArray);
    } catch (error) {
      console.error("Error fetching news:", error);
      // Handle error if needed
    }
  };

  useEffect(() => {
    fetchAllNewsIds();
    fetchNews();
  }, [currentPage]); // Trigger fetchNews when currentPage changes

  useEffect(() => {
    // For example, you can console.log the latestNews to verify if it's updating correctly.
  }, [latestNews]);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xxl">
        <Box sx={{ flexGrow: 1, bgcolor: 'white' }}>

          {/* News List Header */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Item 
                style={{ 
                  paddingTop: '130px', 
                  paddingBottom: '130px',
                  minHeight: '400px', // Ensure the Item has a minimum height 
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/news.jpg)`, 
                  backgroundPosition: '0% 0%',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat'
                }}
              >
              </Item>
            </Grid>
          </Grid>

          {/* News List Section */}
          <Grid container spacing={2} style={{ marginBottom: '25px', marginTop: '25px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h3" text="News List" color="#0651A0" underlineThickness={3} underline={true} />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ paddingTop: '50px', justifyContent: 'center', alignItems: 'center' }}>
            {latestNews.map((newsItem, index) => (
              <Grid item xs={12} sm={12} md={10} lg={10} key={index} style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <NewsComplexCard
                  layout="horizontal"
                  avatarText={currentUser ? currentUser.username.charAt(0).toUpperCase() : 'R'}
                  avatarBgColor='teal'
                  actionIcon
                  title={newsItem.title}

                  image={newsItem.imageURL}
                  content={newsItem.shortDescription}
                  id={newsItem.id}
                  style={{ width: '100%' }} // Ensure card takes full width of its grid item
                />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: '50px' }}>
              <CustomPager
                pageCount={Math.ceil(totalItems / itemsPerPage)}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </Grid>
          </Grid>

          {/* Footer Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12} sm={12} md={12}>
              <Item style={{ paddingTop: '50px', paddingBottom: '50px', backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/footer.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" padding="20px">
                  <Box display="flex" alignItems="center">
                  <img src={`${process.env.PUBLIC_URL}/images/eu-flag.png`} alt="European Union Flag" style={{ width: '100px', height: 'auto', marginRight: '10px' }} />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" flex="1">
                    <CustomTypography variant="h7" text="This project is funded by the European Union's Internal Security Fund — Police." color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" flex="1">
                    <CustomTypography variant="h7" text="Grant Agreement No. 101100558 — PROJECT HECTOR" color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" flex="1">
                    <CustomTypography variant="h7" text="The content of this webpage represents the views of the author only and is his/her sole responsibility. The European Commission does not accept any responsibility for use that may be made of the information it contains." color="white" />
                  </Box>
                </Box>
              </Item>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default News;
